import { useTranslation } from "react-i18next";
import { z } from "zod";
import { type IServiceProviderRegistrationInfo } from "@services/serviceProviders/types";

export const useAddBillSettingsModalSchema = (
  data?: IServiceProviderRegistrationInfo,
  roikCodeRequired?: boolean,
  requiresExistingAgreementOwnerLoginData?: boolean,
) => {
  const { t } = useTranslation();

  const zodRequiredString = z
    .string({
      required_error: t("common.form.field.errors.required"),
    })
    .trim()
    .min(1, { message: t("common.form.field.errors.required") });

  const schema = z
    .object({
      displayName: z.string(),
      customerCode: z
        .string({ required_error: t("common.form.field.errors.required") })
        .min(data?.minCustomerCodeLength ?? 1, {
          message: t("common.form.field.errors.min", {
            num: data?.minCustomerCodeLength ?? 1,
          }),
        })
        .max(data?.maxCustomerCodeLength ?? 0, {
          message: t("common.form.field.errors.max", {
            num: data?.maxCustomerCodeLength ?? 0,
          }),
        }),
      paymentCode: zodRequiredString,
      personalCode: zodRequiredString,
      fullName: zodRequiredString,
      paymentPurpose: zodRequiredString,
      ibanNumber: zodRequiredString,
      dueDay: z.number(),
      roikCode: zodRequiredString,
      existingAgreementOwnerLoginData: z.object({
        username: zodRequiredString,
        password: zodRequiredString,
      }),
    })
    .partial();

  return schema.required({
    customerCode: data?.customerCodeRequired ? true : undefined,
    paymentCode: data?.requiresPaymentCode ? true : undefined,
    personalCode: data?.requiresPersonalCode ? true : undefined,
    fullName: data?.requiresNameSurname ? true : undefined,
    paymentPurpose: data?.requiresPaymentPurpose ? true : undefined,
    ibanNumber: data?.isIbanRequired ? true : undefined,
    roikCode: roikCodeRequired ? true : undefined,
    existingAgreementOwnerLoginData: requiresExistingAgreementOwnerLoginData
      ? true
      : undefined,
  });
};

export type TAddBillSettingsModalSchema = z.infer<
  ReturnType<typeof useAddBillSettingsModalSchema>
>;
